import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { optimizeImage } from '../utils/imageOptimizer';
import './heroSlider.css';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('next');

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth <= 768);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hero-slides`);
        setSlides(Array.isArray(res.data) ? res.data : []);
      } catch {
        setSlides([
          {
            desktopImage: 'https://via.placeholder.com/1920x900/111111/CC1B1B?text=ARCO',
            mobileImage: 'https://via.placeholder.com/390x600/111111/CC1B1B?text=ARCO',
          }
        ]);
      }
    };
    fetchSlides();
  }, []);

  const targetWidth = useMemo(() => (isMobile ? 480 : 1400), [isMobile]);

  const isVideoSlide = useCallback((slide) => {
    return (slide.mediaType === 'video') || !!(isMobile ? slide.mobileVideo : slide.desktopVideo);
  }, [isMobile]);

  const getVideoSrc = useCallback((slide) => {
    return isMobile ? (slide.mobileVideo || slide.desktopVideo) : (slide.desktopVideo || slide.mobileVideo);
  }, [isMobile]);

  const getImageSrc = useCallback((slide) => {
    const rawUrl = isMobile && slide.mobileImage ? slide.mobileImage : (slide.desktopImage || slide.image);
    return optimizeImage(rawUrl, { width: targetWidth, quality: isMobile ? 60 : 75 });
  }, [isMobile, targetWidth]);

  // Preload next image-type slide only (video preloading is handled by the browser)
  useEffect(() => {
    if (!slides.length) return;
    const nextIndex = (currentSlide + 1) % slides.length;
    const next = slides[nextIndex];
    if (next && !isVideoSlide(next)) {
      const img = new Image();
      img.src = getImageSrc(next);
    }
  }, [currentSlide, slides, getImageSrc, isVideoSlide]);

  const changeSlide = useCallback((newIndex, dir = 'next') => {
    if (animating || newIndex === currentSlide) return;
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => {
      setCurrentSlide(newIndex);
      setAnimating(false);
    }, 500);
  }, [animating, currentSlide]);

  const goToNext = useCallback(() => {
    if (!slides.length) return;
    changeSlide((currentSlide + 1) % slides.length, 'next');
  }, [slides.length, currentSlide, changeSlide]);

  const goToPrev = useCallback(() => {
    if (!slides.length) return;
    changeSlide(currentSlide === 0 ? slides.length - 1 : currentSlide - 1, 'prev');
  }, [slides.length, currentSlide, changeSlide]);

  useEffect(() => {
    if (!slides.length) return;
    const t = setInterval(goToNext, 6000);
    return () => clearInterval(t);
  }, [goToNext, slides.length]);

  if (!slides.length) return (
    <div style={{
      width: '100%',
      height: isMobile ? '92vh' : '88vh',
      minHeight: isMobile ? '560px' : '580px',
      maxHeight: '900px',
      background: '#111111',
    }} />
  );

  const slide = slides[currentSlide];

  const hasTitle    = !!slide.title?.trim();
  const hasSubtitle = !!slide.subtitle?.trim();
  const hasTag      = !!slide.tag?.trim();
  const hasCta      = !!slide.ctaText?.trim();
  const hasContent  = hasTitle || hasSubtitle || hasTag || hasCta;

  return (
    <>

      <div className="arco-hero-wrapper">
        <div className="arco-hero">

          {/* Background media */}
          <div className="arco-bg-container">
            {isVideoSlide(slide) ? (
              <video
                key={`video-${currentSlide}`}
                src={getVideoSrc(slide)}
                className={`arco-img ${animating ? `entering-${direction}` : 'active-visible'}`}
                autoPlay muted loop playsInline
              />
            ) : (
              <img
                key={currentSlide}
                src={getImageSrc(slide)}
                alt={slide.title || "ARCO Campaign visual showcase slide"}
                className={`arco-img ${animating ? `entering-${direction}` : 'active-visible'}`}
                fetchPriority={currentSlide === 0 ? 'high' : 'auto'}
                loading="eager"
                decoding={currentSlide === 0 ? 'sync' : 'async'}
              />
            )}
          </div>

          {/* Dark overlay + accent bar, only when there's text to read */}
          {hasContent && <div className="arco-overlay" />}
          {hasContent && <div className="arco-accent" />}

          {/* Content block */}
          {hasContent && (
            <div className="arco-content" key={`content-${currentSlide}`}>
              {hasTag && <div className="arco-tag">{slide.tag}</div>}

              {hasTitle && (
                <h1 className="arco-title">
                  {slide.title.split(' ').map((word, i, arr) =>
                    i === arr.length - 1
                      ? <em key={i}>{word}</em>
                      : <React.Fragment key={i}>{word} </React.Fragment>
                  )}
                </h1>
              )}

              {(hasTitle || hasSubtitle) && <div className="arco-divider" />}

              {hasSubtitle && <p className="arco-sub">{slide.subtitle}</p>}

              {hasCta && (
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem' }}>
                  <a href={slide.ctaLink || '#'} className="arco-cta">
                    <span>{slide.ctaText}</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12,5 19,12 12,19" />
                    </svg>
                  </a>
                  <a href="/catalog" className="arco-cta-ghost">
                    View All
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9,18 15,12 9,6" />
                    </svg>
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Slide counter */}
          {slides.length > 1 && (
            <div className="arco-counter">
              <span className="arco-counter-current">
                {String(currentSlide + 1).padStart(2, '0')}
              </span>
              <div className="arco-counter-bar" />
              <span>{String(slides.length).padStart(2, '0')}</span>
            </div>
          )}


        </div>
      </div>
    </>
  );
};

export default HeroSlider;
